// Shared state conventions for Helix data pages.
//
// usePersistentState — localStorage-backed state, keys namespaced
//   `voice:{page}:{name}` (JSON, safe-parse fallback to the default).
// useDebounced       — debounced derived value (search inputs, 350ms default).
// useAutoRefresh     — interval callback that pauses while the tab is hidden.
// useIsMobile        — true below 768px, the template's phone breakpoint.
// useHelixRoot       — helixRoot() for a template overlay's portal, resolved
//   by the time Radix mounts the portal.

import { useEffect, useLayoutEffect, useRef, useState } from "react";

export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw) as T;
    } catch {
      /* corrupted/legacy value — fall back to default */
    }
    return initial;
  });
  const set = (next: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const v = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
      try {
        localStorage.setItem(key, JSON.stringify(v));
      } catch {
        /* storage full/blocked — state still works in-memory */
      }
      return v;
    });
  };
  return [value, set];
}

export function useDebounced<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function useAutoRefresh(cb: () => void, ms: number, enabled = true): void {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    if (!enabled || ms <= 0) return;
    const tick = () => {
      if (!document.hidden) ref.current();
    };
    const t = setInterval(tick, ms);
    return () => clearInterval(t);
  }, [ms, enabled]);
}

/** Portal target inside the Helix scope so `.hx-*` styles + tokens apply. */
export function helixRoot(): Element {
  return document.querySelector(".helix") ?? document.body;
}

/** helixRoot() as a portal container for a template overlay (ui/dialog.tsx).
 *  An overlay open on the first render runs before the shell's `.helix` is in
 *  the DOM; Radix's Portal renders nothing until its own layout effect, and
 *  this one runs in the same pass, so the first paint is already inside
 *  `.helix` rather than on <body>. Undefined until then (Radix: <body>). */
export function useHelixRoot(): Element | undefined {
  const [el, setEl] = useState<Element | undefined>(() => document.querySelector(".helix") ?? undefined);
  useLayoutEffect(() => {
    if (!el) setEl(helixRoot());
  }, [el]);
  return el;
}

/** True below 768px, the template's phone breakpoint, following the window
 *  as it resizes (next-shadcn-admin-dashboard/src/hooks/use-mobile.ts at
 *  bff60d4). The sidebar turns into a sheet below it. `components/ui` files
 *  import it from here, never through the hx barrel. */
const MOBILE_BREAKPOINT = 768;
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return !!isMobile;
}
